import {
  useState,
  type ChangeEvent,
  type FocusEvent,
  type FocusEventHandler,
  type KeyboardEvent,
  type KeyboardEventHandler,
} from "react";

/* The value side of NumberInput. Internal to the atom: not in the barrel.

   onChange mode (today's): the field shows `value` and reports every
   keystroke that parses. The caller's focus and key handlers pass through
   untouched.

   Draft mode (`onCommit`): the field keeps the typed text as a draft, so
   "1.", "-" and "0.0" stay on screen. Blur or Enter commits an edited draft
   when it is finite or empty; a non-finite draft is never committed and
   stays, invalid, until it is fixed or Escape reverts it. A `value` from
   outside re-seeds the draft only while the field is unfocused, so it never
   overwrites what is being typed. A step press commits at once and
   re-seeds. The caller's onFocus, onBlur and onKeyDown run first; calling
   `preventDefault()` in onBlur or onKeyDown skips the commit or revert for
   that event. */

export type NumberValue = number | "";

interface Draft {
  /** The committed value the draft was seeded from. */
  readonly from: NumberValue;
  readonly text: string;
  /** Only a draft the user typed into commits. */
  readonly edited: boolean;
}

const seed = (value: NumberValue): Draft => ({
  from: value,
  text: value === "" ? "" : String(value),
  edited: false,
});

/** "" for blank text, the number when it is finite, otherwise undefined. */
function parse(text: string): NumberValue | undefined {
  const trimmed = text.trim();
  if (trimmed === "") return "";
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : undefined;
}

interface NumberFieldArgs {
  readonly value: NumberValue;
  readonly onChange: ((value: NumberValue) => void) | undefined;
  readonly onCommit: ((value: NumberValue, text: string) => void) | undefined;
  readonly onFocus: FocusEventHandler<HTMLInputElement> | undefined;
  readonly onBlur: FocusEventHandler<HTMLInputElement> | undefined;
  readonly onKeyDown: KeyboardEventHandler<HTMLInputElement> | undefined;
}

export function useNumberField({
  value,
  onChange,
  onCommit,
  onFocus,
  onBlur,
  onKeyDown,
}: NumberFieldArgs) {
  const [focused, setFocused] = useState(false);
  const [stored, setStored] = useState(() => seed(value));

  if (!onCommit) {
    return {
      text: value === "" ? "" : String(value),
      invalid: false,
      stepBase: value,
      onStep: (next: number) => onChange?.(next),
      handlers: {
        onFocus,
        onBlur,
        onKeyDown,
        onChange: (e: ChangeEvent<HTMLInputElement>) => {
          const raw = e.target.value;
          if (raw === "") {
            onChange?.("");
            return;
          }
          const n = Number(raw);
          if (Number.isFinite(n)) onChange?.(n);
        },
      },
    };
  }

  // Re-seed only while unfocused.
  const draft = focused || stored.from === value ? stored : seed(value);
  const parsed = parse(draft.text);
  const commit = () => {
    if (!draft.edited || parsed === undefined) return;
    const text = draft.text.trim();
    onCommit(parsed, text);
    setStored({ from: parsed, text, edited: false });
  };
  const onStep = (next: number) => {
    onCommit(next, String(next));
    setStored(seed(next));
  };

  return {
    text: draft.text,
    invalid: draft.edited && parsed === undefined,
    // A step starts from the draft, or from the committed value when the
    // draft is not a number.
    stepBase: parsed ?? value,
    onStep,
    handlers: {
      onFocus: (e: FocusEvent<HTMLInputElement>) => {
        onFocus?.(e);
        setFocused(true);
        if (draft !== stored) setStored(draft);
      },
      onBlur: (e: FocusEvent<HTMLInputElement>) => {
        onBlur?.(e);
        setFocused(false);
        if (!e.defaultPrevented) commit();
      },
      onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => {
        onKeyDown?.(e);
        if (e.defaultPrevented) return;
        if (e.key === "Enter") commit();
        // Escape reverts; with nothing to revert it reaches the parent (a
        // dialog still closes).
        if (e.key === "Escape" && (draft.edited || draft.from !== value)) {
          e.preventDefault();
          e.stopPropagation();
          setStored(seed(value));
        }
      },
      onChange: (e: ChangeEvent<HTMLInputElement>) =>
        setStored({ from: draft.from, text: e.target.value, edited: true }),
    },
  };
}
